import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen, Package, Link2, CheckCircle, AlertTriangle, Loader2, Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";

interface VendorCatalog {
  id: string;
  vendorId: string | null;
  name: string;
  description: string | null;
  status: string;
  vendor?: { id: string; name: string };
}

interface CatalogItem {
  id: string;
  catalogId: string;
  sku: string;
  name: string;
  description: string | null;
  uom: string | null;
  unitCost: string | null;
  unitPrice: string | null;
  calculatedPrice: string | null;
  priceSource: string;
  productClass: string;
  minimumOrderQuantity: number | null;
  leadTimeDays: number | null;
}

interface CatalogAlert {
  id: string;
  catalogId: string | null;
  updateType: string;
  previousVersion: string | null;
  newVersion: string | null;
  changeSummary: string | null;
  status: string;
  createdAt: string;
}

function fmtPrice(v: string | null | undefined) {
  if (!v) return "-";
  const n = parseFloat(v);
  if (isNaN(n)) return "-";
  return `$${n.toFixed(2)}`;
}

export default function CatalogBrowserPage() {
  const { sessionId } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [selectedCatalog, setSelectedCatalog] = useState<VendorCatalog | null>(null);
  const [search, setSearch] = useState("");
  const [linkingItem, setLinkingItem] = useState<CatalogItem | null>(null);

  const { data: catalogs = [], isLoading: catalogsLoading } = useQuery<VendorCatalog[]>({
    queryKey: ["/api/catalog-browser/catalogs"],
    queryFn: () => api.get<VendorCatalog[]>(`/catalog-browser/catalogs`, sessionId),
  });

  const { data: itemsData, isLoading: itemsLoading } = useQuery<{ items: CatalogItem[]; total: number }>({
    queryKey: ["/api/catalog-browser/catalogs", selectedCatalog?.id, "items", { search }],
    queryFn: () =>
      api.get<{ items: CatalogItem[]; total: number }>(
        `/catalog-browser/catalogs/${selectedCatalog!.id}/items?search=${encodeURIComponent(search)}`,
        sessionId
      ),
    enabled: !!selectedCatalog,
  });

  const { data: alerts = [] } = useQuery<CatalogAlert[]>({
    queryKey: ["/api/catalog-alerts"],
    queryFn: () => api.get<CatalogAlert[]>(`/catalog-alerts`, sessionId),
  });

  const linkMutation = useMutation({
    mutationFn: (itemId: string) => api.post(`/catalog-browser/items/${itemId}/link`, {}, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      queryClient.invalidateQueries({ queryKey: ["/api/catalog-browser/catalogs"] });
      setLinkingItem(null);
      toast({ title: "Product created from catalog item" });
    },
    onError: (e: Error) =>
      toast({ title: "Failed to link item", description: e.message, variant: "destructive" }),
  });

  const acceptMutation = useMutation({
    mutationFn: (id: string) => api.post(`/catalog-alerts/${id}/accept`, {}, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/catalog-alerts"] });
      toast({ title: "Update accepted" });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (id: string) => api.post(`/catalog-alerts/${id}/dismiss`, {}, sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/catalog-alerts"] });
      toast({ title: "Update dismissed" });
    },
  });

  const pendingAlerts = alerts.filter(a => a.status === "pending");
  const items = itemsData?.items || [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catalog Browser"
        description="Browse vendor catalogs and add products to your inventory"
      />

      {pendingAlerts.length > 0 && (
        <Card className="border-amber-300 dark:border-amber-700">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Pending Updates ({pendingAlerts.length})
            </CardTitle>
            <CardDescription>
              Vendor catalogs have been updated. Review changes below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingAlerts.slice(0, 5).map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-3 bg-muted rounded-md">
                  <div>
                    <p className="text-sm font-medium">{alert.changeSummary || "Catalog update"}</p>
                    <p className="text-xs text-muted-foreground">
                      {alert.updateType === "price_change" && "Price changed"}
                      {alert.updateType === "spec_change" && "Specifications updated"}
                      {alert.updateType === "discontinued" && "Product discontinued"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => dismissMutation.mutate(alert.id)}>
                      Dismiss
                    </Button>
                    <Button size="sm" onClick={() => acceptMutation.mutate(alert.id)}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Accept
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="browse" className="space-y-4">
        <TabsList>
          <TabsTrigger value="browse">Browse Catalogs</TabsTrigger>
          <TabsTrigger value="alerts">
            Update Alerts
            {pendingAlerts.length > 0 && (
              <Badge variant="secondary" className="ml-2">{pendingAlerts.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-1 space-y-2">
              <h3 className="text-sm font-medium mb-2">Available Catalogs</h3>
              {catalogsLoading ? (
                <div className="text-sm text-muted-foreground">Loading...</div>
              ) : catalogs.length === 0 ? (
                <div className="text-sm text-muted-foreground">No catalogs available</div>
              ) : (
                catalogs.map((catalog) => (
                  <Card
                    key={catalog.id}
                    className={`cursor-pointer transition-colors ${selectedCatalog?.id === catalog.id ? "border-primary" : "hover:bg-muted/40"}`}
                    onClick={() => { setSelectedCatalog(catalog); setSearch(""); }}
                  >
                    <CardHeader className="p-3">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        {catalog.name}
                      </CardTitle>
                      {catalog.vendor?.name && (
                        <CardDescription className="text-xs truncate">
                          {catalog.vendor.name}
                        </CardDescription>
                      )}
                    </CardHeader>
                  </Card>
                ))
              )}
            </div>

            <div className="md:col-span-3">
              {selectedCatalog ? (
                <Card>
                  <CardContent className="p-0">
                    <div className="p-4 border-b">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search products..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                    </div>
                    {itemsLoading ? (
                      <div className="text-center py-12 text-muted-foreground">Loading products...</div>
                    ) : items.length === 0 ? (
                      <div className="text-center py-12">
                        <Package className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                        <p className="text-muted-foreground">No products in this catalog</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-border">
                          <thead className="bg-muted/40">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Product</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">SKU</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">UOM</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Cost</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">MOQ</th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Lead Time</th>
                              <th className="px-6 py-3"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {items.map((row) => (
                              <tr key={row.id} className="hover:bg-muted/40">
                                <td className="px-6 py-3 text-sm font-medium">{row.name}</td>
                                <td className="px-6 py-3 text-sm text-muted-foreground">{row.sku}</td>
                                <td className="px-6 py-3 text-sm text-muted-foreground">{row.uom || "EA"}</td>
                                <td className="px-6 py-3 text-sm text-right">{fmtPrice(row.unitCost)}</td>
                                <td className="px-6 py-3 text-sm text-right text-muted-foreground">
                                  {row.minimumOrderQuantity ?? "-"}
                                </td>
                                <td className="px-6 py-3 text-sm text-right text-muted-foreground">
                                  {row.leadTimeDays != null ? `${row.leadTimeDays}d` : "-"}
                                </td>
                                <td className="px-6 py-3 text-right">
                                  <Button size="sm" onClick={() => setLinkingItem(row)}>
                                    <Link2 className="h-4 w-4 mr-1" />
                                    Add to Products
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <BookOpen className="h-12 w-12 mb-4" />
                  <p>Select a catalog to browse products</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          {alerts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <CheckCircle className="h-12 w-12 mb-4" />
              <p>No update alerts</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <Card key={alert.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm flex items-center gap-2">
                        {alert.updateType === "price_change" && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                        {alert.updateType === "discontinued" && <AlertTriangle className="h-4 w-4 text-destructive" />}
                        {alert.changeSummary || "Catalog update"}
                      </CardTitle>
                      <Badge variant={alert.status === "pending" ? "secondary" : alert.status === "accepted" ? "default" : "outline"}>
                        {alert.status}
                      </Badge>
                    </div>
                    {alert.previousVersion && alert.newVersion && (
                      <CardDescription>
                        Version {alert.previousVersion} → {alert.newVersion}
                      </CardDescription>
                    )}
                  </CardHeader>
                  {alert.status === "pending" && (
                    <CardContent className="pt-0">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => dismissMutation.mutate(alert.id)}>
                          Dismiss
                        </Button>
                        <Button size="sm" onClick={() => acceptMutation.mutate(alert.id)}>
                          Accept Update
                        </Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!linkingItem} onOpenChange={(open) => !open && setLinkingItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Products</DialogTitle>
            <DialogDescription>
              This will create a new product in your inventory linked to the vendor catalog item.
              Future updates from the vendor will be tracked.
            </DialogDescription>
          </DialogHeader>
          {linkingItem && (
            <div className="p-4 bg-muted rounded-md space-y-2">
              <p className="font-medium">{linkingItem.name}</p>
              <p className="text-sm text-muted-foreground">SKU: {linkingItem.sku}</p>
              <div className="flex gap-4 text-sm">
                <span>Cost: {fmtPrice(linkingItem.unitCost)}</span>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setLinkingItem(null)}>Cancel</Button>
            <Button
              onClick={() => linkingItem && linkMutation.mutate(linkingItem.id)}
              disabled={linkMutation.isPending}
            >
              {linkMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create Product
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
